import { useQueryClient } from '@tanstack/react-query';
import PQueue from 'p-queue';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GeodataMarker } from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useSubscribeCallback } from '../../../hooks/use-subscribe';
import { useToaster } from '../../../state/toaster-state';

const CONCURRENCY = 1;

/** Compare payload fields we send to the API; used to detect if cache was updated by a newer request. */
function isSameMarkerPayload(a: GeodataMarker, b: GeodataMarker): boolean {
  return (
    a.id === b.id &&
    a.imageId === b.imageId &&
    a.gcpId === b.gcpId &&
    a.pinned === b.pinned &&
    a.position[0] === b.position[0] &&
    a.position[1] === b.position[1]
  );
}

/**
 * Queues marker update (putMarker) requests with constrained concurrency.
 * Optimistic: patches the getMarkers cache immediately, then reverts on failure.
 * Shows a toaster when any patch request fails.
 */
export function useUpdateMarkerQueue(geodataId: string) {
  const queryClient = useQueryClient();
  const { showToast } = useToaster();
  const { t } = useTranslation('cloudProcessing');

  const queryKey = useMemo(() => ['getGeodataMarkers', geodataId] as const, [geodataId]);

  const patchMarkers = useCallback(
    (newMarkers: GeodataMarker[]) => {
      queryClient.setQueryData<GeodataMarker[] | undefined>(queryKey, (prev) => {
        const current = prev ?? [];
        if (newMarkers.length === 0) {
          return prev;
        }

        const byId = new Map(current.map((m) => [m.id, m]));
        let changed = false;

        for (const incoming of newMarkers) {
          const existing = byId.get(incoming.id);
          const merged = existing ? { ...existing, ...incoming } : incoming;
          // Shallow compare to avoid re-setting identical objects.
          if (
            !existing ||
            Object.keys(merged).some((k) => (existing as any)[k] !== (merged as any)[k])
          ) {
            byId.set(incoming.id, merged);
            changed = true;
          }
        }

        if (!changed) {
          return prev;
        } // preserve referential equality
        return Array.from(byId.values());
      });
    },
    [queryClient, queryKey],
  );
  const [pendingUpdateCount, setPendingUpdateCount] = useState(0);

  const pQueue = useRef(
    new PQueue({
      concurrency: CONCURRENCY,
      autoStart: true,
    }),
  );

  const queueUpdateMarker = useCallback(
    (newMarker: GeodataMarker) => {
      const previousData = queryClient.getQueryData<GeodataMarker[]>(queryKey);
      const previousMarker = previousData?.find((m) => m.id === newMarker.id);

      // Optimistic update
      patchMarkers([newMarker]);

      setPendingUpdateCount((c) => c + 1);
      void pQueue.current.add(async () => {
        try {
          await SkyMapAxiosServiceFactory.instance.createGeodataServiceV0().putMarker({
            path: {
              geodataId,
              markerId: newMarker.id,
            },
            body: {
              gcpId: newMarker.gcpId,
              imageId: newMarker.imageId,
              pinned: newMarker.pinned,
              position: newMarker.position,
            },
          });
        } catch {
          // Only revert if the cache still has our optimistic update (no newer request overwrote it).
          const currentData = queryClient.getQueryData<GeodataMarker[]>(queryKey);
          const currentMarker = currentData?.find((m) => m.id === newMarker.id);
          const ourUpdateStillCurrent =
            currentMarker !== undefined && isSameMarkerPayload(currentMarker, newMarker);

          if (ourUpdateStillCurrent) {
            if (previousMarker) {
              patchMarkers([previousMarker]);
            } else {
              queryClient.setQueryData<GeodataMarker[] | undefined>(queryKey, (prev) =>
                prev?.filter((m) => m.id !== newMarker.id),
              );
            }
            showToast({
              type: 'error',
              title: t('toaster.title.error', { ns: 'common' }),
              message: t('reviewGcpMarkerEstimatesDialog.someMarkersFailedToUpdate', {
                ns: 'cloudProcessing',
              }),
            });
          }
        } finally {
          setPendingUpdateCount((c) => c - 1);
        }
      });
    },
    [geodataId, queryKey, patchMarkers, queryClient, showToast, t],
  );

  const isPendingMarkerUpdates = pendingUpdateCount > 0;

  useEffect(() => {
    const queue = pQueue.current;
    return () => queue.clear();
  }, []);

  useSubscribeCallback(SubscriptionTopic.WebsocketMessageRecieved, (event) => {
    if (event.message === 'GeodataImageUpdated' && event.geodataId === geodataId) {
      patchMarkers(event.markers);
    }
  });

  return { queueUpdateMarker, isPendingMarkerUpdates };
}
